import React, { useEffect, useState } from 'react';
import { View, Image, Platform, Linking, useWindowDimensions } from 'react-native';
import { SwipeCard, SwipeDeckFrame, ProfileFilterButton } from './discovery';
import {
  CURRENT_USER,
  newId,
  skills,
  industries,
  stages,
  commitmentMetrics,
  activeProjectCap,
  type Venture,
  type Command,
} from '@ventureroom/core';
import { useApp, useData, useTheme, useMe } from './provider';
import {
  Avatar,
  Button,
  Card,
  Column,
  Copy,
  Empty,
  Field,
  FormSheet,
  FormFields,
  type FormValues,
  Heading,
  Input,
  Metric,
  Page,
  Row,
  Segments,
  Sheet,
  Tag,
  dateLabel,
  timeLabel,
} from './components';
import { Conversation } from './connections';
function ChatPane({ children }: { children: React.ReactNode }) {
  const { height } = useWindowDimensions();
  return <View style={{ height: Math.max(460, height - 300) }}>{children}</View>;
}
import {
  DecisionPanel,
  CommitmentPanel,
  CalendarPanel,
  ContributionPanel,
} from './venture-records';
export function Ventures() {
  const data = useData();
  const app = useApp();
  const [create, setCreate] = useState(false);
  const me = useMe();
  const atCap =
    data.ventures.filter((v) => v.members.includes(CURRENT_USER) && v.stage !== 'Closed').length >=
    activeProjectCap(me);
  const [view, setView] = useState('Yours');
  const [query, setQuery] = useState('');
  const [industry, setIndustry] = useState('All industries');
  const [stage, setStage] = useState('All stages');
  const ventures = data.ventures.filter((v) => {
    if (view === 'Yours') return v.members.includes(CURRENT_USER);
    if (industry !== 'All industries' && v.industry !== industry) return false;
    if (stage !== 'All stages' && v.stage !== stage) return false;
    return [
      v.name,
      ...v.members.flatMap((id) => {
        const p =
          data.profiles.find((p) => p.id === id) ?? v.memberSummaries?.find((p) => p.id === id);
        return [p?.name ?? '', p?.username ?? id];
      }),
    ].some((text) => text.toLowerCase().includes(query.trim().toLowerCase()));
  });
  return (
    <Page title="Ventures">
      <Segments items={['Yours', 'Explore']} value={view} onChange={setView} />
      {view === 'Explore' && (
        <Column gap={12}>
          <Input
            label="Search ventures"
            value={query}
            onChange={setQuery}
            placeholder="Venture name, member name or username"
          />
          <Segments
            items={[
              'All industries',
              ...new Set(data.ventures.map((v) => v.industry).filter(Boolean)),
            ]}
            value={industry}
            onChange={setIndustry}
          />
          <Segments items={['All stages', ...stages]} value={stage} onChange={setStage} />
        </Column>
      )}
      <Column gap={0}>
        {ventures.map((v) => (
          <Card
            key={v.id}
            style={{ width: '100%' }}
            onPress={() => app.navigate(`venture/${v.id}`)}
            label={`Open ${v.name}`}
          >
            <Row style={{ alignItems: 'flex-start' }}>
              {v.logo ? (
                <Image
                  source={{ uri: v.logo }}
                  accessibilityLabel={`${v.name} logo`}
                  style={{ width: 44, height: 44, borderRadius: 12 }}
                  resizeMode="contain"
                />
              ) : (
                <Avatar
                  person={{ name: v.name, avatar: v.stage === 'Closed' ? 'sand' : 'sage' }}
                  size={44}
                />
              )}
              <Column gap={6} style={{ flex: 1, minWidth: 0 }}>
                <Heading size={21}>{v.name}</Heading>
                <Copy muted size={12}>
                  {v.stage} · {v.members.length} {v.members.length === 1 ? 'member' : 'members'}
                </Copy>
                <Copy size={14} lines={2}>
                  {v.description || v.pitch}
                </Copy>
              </Column>
              <Copy muted size={20}>
                ›
              </Copy>
            </Row>
          </Card>
        ))}
      </Column>
      {!ventures.length && (
        <Empty
          title="Give your idea a room"
          detail="Start a venture and invite the people you’d like to build with."
        />
      )}
      <Button disabled={atCap || me.role === 'Skill Provider'} onPress={() => setCreate(true)}>
        Create a new venture
      </Button>
      {atCap && (
        <Copy muted>
          Your free plan includes five active ventures. Close one or upgrade to create another.
        </Copy>
      )}
      {create && <CreateVentureSheet onClose={() => setCreate(false)} />}
    </Page>
  );
}
const ventureDetailFields: Field[] = [
  { key: 'name', label: 'Venture name', required: true },
  { key: 'logo', label: 'Venture logo (optional)', type: 'logo' },
  { key: 'description', label: 'Public venture description', type: 'long', required: true },
  { key: 'pitch', label: 'The idea in one sentence' },
  {
    key: 'mission',
    label: 'Mission statement',
    type: 'long',
    hint: 'Public: shown to prospective collaborators.',
  },
  { key: 'problem', label: 'What problem will you solve?', type: 'long' },
  { key: 'solution', label: 'Your proposed solution', type: 'long' },
  {
    key: 'ownedAssets',
    label: 'What do you already own? (optional)',
    type: 'long',
    hint: 'Assets, software, equipment, patents or other IP. Visible to your venture team.',
  },
  {
    key: 'websiteUrl',
    label: 'Existing website (optional)',
    hint: 'https://example.com — shown on your public venture overview',
  },
  {
    key: 'industry',
    label: 'Industry',
    type: 'select',
    options: [...industries],
    allowOther: true,
  },
  { key: 'stage', label: 'Current stage', type: 'select', options: [...stages] },
  { key: 'seeking', label: 'Skills you still need', type: 'multi', options: [...skills] },
  { key: 'milestone', label: 'Your next small milestone' },
  { key: 'cadence', label: 'How will you work together?' },
  { key: 'hours', label: 'Expected hours per week', type: 'number' },
  {
    key: 'preference',
    label: 'Working location',
    type: 'select',
    options: ['Remote', 'Local', 'Hybrid'],
  },
  { key: 'location', label: 'Broad location' },
];
export function CreateVentureSheet({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated?: () => Promise<unknown>;
}) {
  const data = useData();
  const app = useApp();
  const connections = data.connections
    .filter((c) => c.status === 'accepted' && [c.from, c.to].includes(CURRENT_USER))
    .map((c) => data.profiles.find((p) => p.id === (c.from === CURRENT_USER ? c.to : c.from))!);
  const fields: Field[] = [
    ...ventureDetailFields,
    {
      key: 'members',
      label: 'Invite your connections',
      type: 'multi',
      options: connections.map((p) => p.name),
      hint: 'They will receive an invitation and choose whether to join.',
    },
  ];
  return (
    <FormSheet
      title="Give your idea a room"
      intro="Only venture name and public venture description are required. Everything else is optional."
      fields={fields}
      initial={{
        name: '',
        logo: '',
        pitch: '',
        mission: '',
        description: '',
        problem: '',
        solution: '',
        ownedAssets: '',
        websiteUrl: '',
        industry: '',
        stage: 'Forming Team',
        members: [],
        seeking: [],
        milestone: '',
        cadence: '',
        hours: '',
        preference: 'Remote',
        location: '',
      }}
      onClose={onClose}
      submit="Create venture"
      onSave={async (v) => {
        const id = newId('venture');
        const command = {
          type: 'venture',
          venture: {
            ...v,
            id,
            description: String(v.description),
            hours: Number(v.hours || 0),
            members: [
              CURRENT_USER,
              ...connections
                .filter((p) => (v.members as string[]).includes(p.name))
                .map((p) => p.id),
            ],
            createdAt: new Date().toISOString(),
          },
        } as Command;
        const ok = await app.act(command, 'Your venture has a home');
        if (ok) {
          await onCreated?.();
          app.navigate(`venture/${id}`);
        }
        return ok;
      }}
    />
  );
}
export function VentureOnboarding() {
  const app = useApp();
  const [creating, setCreating] = useState(false);
  if (!app.authenticated || !app.data || !app.onboardingPending) return null;
  if (creating)
    return (
      <CreateVentureSheet
        onClose={() => setCreating(false)}
        onCreated={() => app.completeOnboarding()}
      />
    );
  return (
    <Sheet title="Do you already have a venture?" onClose={() => void app.completeOnboarding()}>
      <Copy>
        Give your idea a home. Add what you want to build, what you already own and an existing
        website if you have one.
      </Copy>
      <Button onPress={() => setCreating(true)}>Create one here</Button>
      <Button kind="quiet" onPress={() => void app.completeOnboarding()} disabled={app.busy}>
        Not now
      </Button>
      <Copy muted size={12}>
        You can create a venture later from the Ventures tab.
      </Copy>
    </Sheet>
  );
}
function VentureWebsite({ url }: { url?: string }) {
  const app = useApp();
  return url ? (
    <Column gap={6}>
      <Copy muted>{url}</Copy>
      <Row>
        <Button
          kind="secondary"
          onPress={() =>
            void Linking.openURL(url).catch(() => app.fail('This website could not be opened.'))
          }
        >
          Visit website
        </Button>
      </Row>
    </Column>
  ) : null;
}
function Overview({ venture: v }: { venture: Venture }) {
  const data = useData();
  const app = useApp();
  const t = useTheme();
  const tasks = data.commitments.filter((c) => c.ventureId === v.id),
    events = data.events
      .filter((e) => e.ventureId === v.id && e.startsAt >= new Date().toISOString())
      .sort((a, b) => a.startsAt.localeCompare(b.startsAt)),
    decisions = data.decisions.filter(
      (d) => d.ventureId === v.id && !d.closedAt && d.deadline > new Date().toISOString(),
    );
  return (
    <Column gap={24}>
      <Column gap={8}>
        <Heading size={21}>Mission</Heading>
        <Copy>{v.mission || v.solution}</Copy>
        <Copy muted>{v.description}</Copy>
        <VentureWebsite url={v.websiteUrl} />
      </Column>
      {!!v.ownedAssets && (
        <Column gap={8}>
          <Heading size={21}>What we already own</Heading>
          <Copy>{v.ownedAssets}</Copy>
        </Column>
      )}
      <Card>
        <Copy color={t.heroInk} size={11} bold>
          Next milestone
        </Copy>
        <Heading size={30}>{v.milestone}</Heading>
        <Copy color={t.heroInk}>{v.cadence}</Copy>
      </Card>
      <Card>
        <Row wrap>
          <Metric
            value={tasks.filter((c) => c.status === 'Completed').length}
            label="commitments completed"
          />
          <Metric
            value={tasks.filter((c) => c.status !== 'Completed').length}
            label="active commitments"
          />
          <Metric value={decisions.length} label="open decisions" />
        </Row>
      </Card>
      <Row wrap style={{ alignItems: 'stretch' }}>
        <Card style={{ flex: 1, minWidth: 260 }}>
          <Heading size={21}>Why this exists</Heading>
          <Copy>{v.problem}</Copy>
          <Copy muted>{v.solution}</Copy>
        </Card>
        <Card style={{ flex: 1, minWidth: 260 }}>
          <Heading size={21}>Next meeting</Heading>
          {events[0] ? (
            <>
              <Tag>
                {dateLabel(events[0].startsAt)} · {timeLabel(events[0].startsAt)}
              </Tag>
              <Copy bold>{events[0].title}</Copy>
              <Copy muted>{events[0].details}</Copy>
            </>
          ) : (
            <Copy muted>No meeting planned yet. Add one in Calendar.</Copy>
          )}
        </Card>
      </Row>
      <Heading size={21}>Team</Heading>
      <Row wrap>
        {v.members.map((id) => {
          const p = data.profiles.find((p) => p.id === id)!;
          return (
            <Card
              key={id}
              style={{ flex: 1, minWidth: 200 }}
              onPress={() => app.navigate(`person/${id}`)}
            >
              <Avatar person={p} />
              <Copy bold>{p.name}</Copy>
              <Copy muted size={12}>
                {p.skills.slice(0, 2).join(' · ')}
              </Copy>
            </Card>
          );
        })}
      </Row>
      <Heading size={21}>Recent activity</Heading>
      {data.activities
        .filter((a) => a.ventureId === v.id)
        .slice(0, 5)
        .map((a) => (
          <Row key={a.id} between>
            <Column gap={3} style={{ flex: 1 }}>
              <Copy bold size={14}>
                {a.title}
              </Copy>
              <Copy muted size={12}>
                {a.detail}
              </Copy>
            </Column>
            <Copy muted size={11}>
              {dateLabel(a.createdAt)}
            </Copy>
          </Row>
        ))}
    </Column>
  );
}
function InviteSearch({ venture: v, onClose }: { venture: Venture; onClose: () => void }) {
  const data = useData(),
    app = useApp();
  const [query, setQuery] = useState('');
  const people =
    query.trim().length >= 2
      ? data.profiles
          .filter(
            (p) =>
              !v.members.includes(p.id) &&
              [p.username ?? p.id, p.name].some((value) =>
                value.toLowerCase().includes(query.trim().replace(/^@/, '').toLowerCase()),
              ),
          )
          .slice(0, 20)
      : [];
  return (
    <Sheet title="Invite a teammate" onClose={onClose}>
      <Input
        label="Find a username"
        value={query}
        onChange={setQuery}
        placeholder="Type a unique username"
      />
      {people.map((p) => {
        const pending = data.invitations.find(
          (i) =>
            i.ventureId === v.id &&
            i.kind === 'invitation' &&
            i.to === p.id &&
            i.status === 'pending',
        );
        return (
          <Row key={p.id} between>
            <Button
              kind="quiet"
              label={`View profile ${p.username ?? p.id}`}
              onPress={() => app.navigate(`person/${p.id}`)}
            >
              {p.name} · @{p.username ?? p.id}
            </Button>
            <Button
              disabled={app.busy}
              label={`${pending ? 'Uninvite' : 'Invite'} ${p.username ?? p.id}`}
              onPress={() =>
                void app.act(
                  pending
                    ? {
                        type: 'invitationCancel',
                        id: pending.id,
                      }
                    : { type: 'invite', ventureId: v.id, personId: p.id },
                  pending ? 'Invitation cancelled' : 'Invitation sent',
                )
              }
            >
              {pending ? 'Uninvite' : 'Invite'}
            </Button>
          </Row>
        );
      })}
      {query.trim().length >= 2 && !people.length && <Copy muted>No matching usernames.</Copy>}
    </Sheet>
  );
}
function matchesTeam(
  person: NonNullable<ReturnType<typeof useData>>['profiles'][number],
  query: string,
  kind: string,
  note = '',
) {
  return (
    (kind === 'Everyone' || (kind === 'AI collaborators') === !!person.is_ai) &&
    [person.name, person.username ?? person.id, ...person.skills, ...person.brings, note]
      .join(' ')
      .toLowerCase()
      .includes(query.trim().toLowerCase())
  );
}
function TeamFilters({
  query,
  setQuery,
  kind,
  setKind,
  onClose,
}: {
  query: string;
  setQuery: (v: string) => void;
  kind: string;
  setKind: (v: string) => void;
  onClose: () => void;
}) {
  return (
    <Sheet title="Team filters" onClose={onClose}>
      <Input
        label="Find a teammate or skill"
        value={query}
        onChange={setQuery}
        placeholder="Name, username, skill or request note"
      />
      <Segments
        items={['Everyone', 'Humans', 'AI collaborators']}
        value={kind}
        onChange={setKind}
      />
      <Button
        kind="quiet"
        onPress={() => {
          setQuery('');
          setKind('Everyone');
        }}
      >
        Clear filters
      </Button>
      <Button onPress={onClose}>Show results</Button>
    </Sheet>
  );
}
function PendingRequests({ venture: v, onBack }: { venture: Venture; onBack: () => void }) {
  const data = useData(),
    app = useApp(),
    t = useTheme();
  const [filters, setFilters] = useState(false),
    [query, setQuery] = useState(''),
    [kind, setKind] = useState('Everyone');
  const allPending = data.invitations.filter(
    (i) =>
      i.ventureId === v.id &&
      i.kind === 'request' &&
      i.status === 'pending' &&
      i.to === CURRENT_USER,
  );
  const pending = allPending.filter((i) => {
    const p = data.profiles.find((p) => p.id === i.from);
    return p && matchesTeam(p, query, kind, i.note);
  });
  const request = pending[0],
    person = data.profiles.find((p) => p.id === request?.from);
  return (
    <View style={{ flex: 1, minHeight: 0, padding: 6, gap: 8, backgroundColor: t.background }}>
      <Row>
        <Button kind="quiet" label="Back to team" onPress={onBack}>
          ←
        </Button>
        <Heading size={20}>Pending requests · {pending.length}</Heading>
      </Row>
      <SwipeDeckFrame>
        {request && person ? (
          <SwipeCard
            key={request.id}
            person={person}
            note={request.note || 'No collaboration note attached.'}
            review
            onFilters={() => setFilters(true)}
            onAction={(action) => {
              if (action === 'share') {
                app.navigate(`person/${person.id}`);
                return Promise.resolve(false);
              }
              return app.act(
                { type: 'invitationRespond', id: request.id, accept: action === 'like' },
                action === 'like' ? 'Collaborator accepted' : 'Request declined',
              );
            }}
          />
        ) : (
          <Column>
            <Empty
              title={allPending.length ? 'No matching requests' : 'No pending requests'}
              detail={
                allPending.length
                  ? 'Try another filter.'
                  : 'New collaboration requests will appear here.'
              }
            />
            <ProfileFilterButton label="Team request filters" onPress={() => setFilters(true)} />
          </Column>
        )}
      </SwipeDeckFrame>
      {filters && (
        <TeamFilters
          query={query}
          setQuery={setQuery}
          kind={kind}
          setKind={setKind}
          onClose={() => setFilters(false)}
        />
      )}
      {!!app.error && (
        <Copy color={t.danger} size={12}>
          {app.error}
        </Copy>
      )}
    </View>
  );
}
function Team({ venture: v, onReview }: { venture: Venture; onReview: () => void }) {
  const data = useData();
  const app = useApp();
  const [invite, setInvite] = useState(false),
    [endorse, setEndorse] = useState<string | null>(null);
  const owner = v.members[0] === CURRENT_USER;
  const [teamView, setTeamView] = useState('Members');
  const pendingInvites = data.invitations.filter(
    (i) => i.ventureId === v.id && i.kind === 'invitation' && i.status === 'pending',
  );
  const [filters, setFilters] = useState(false),
    [query, setQuery] = useState(''),
    [kind, setKind] = useState('Everyone');
  return (
    <Column gap={20}>
      <Row between>
        <Heading size={22}>Team</Heading>
        <Row wrap>
          <ProfileFilterButton label="Team filters" onPress={() => setFilters(true)} />
          {owner && <Button onPress={() => setInvite(true)}>+ Invite</Button>}
        </Row>
      </Row>
      {filters && (
        <TeamFilters
          query={query}
          setQuery={setQuery}
          kind={kind}
          setKind={setKind}
          onClose={() => setFilters(false)}
        />
      )}
      {owner && (
        <Segments
          items={['Members', `Pending invites (${pendingInvites.length})`]}
          value={teamView === 'Members' ? teamView : `Pending invites (${pendingInvites.length})`}
          onChange={(value) => setTeamView(value === 'Members' ? 'Members' : 'Pending invites')}
        />
      )}
      {owner && teamView === 'Pending invites' ? (
        <Column gap={12}>
          {pendingInvites
            .filter((i) => {
              const p = data.profiles.find((p) => p.id === i.to);
              return p && matchesTeam(p, query, kind);
            })
            .map((i) => {
              const p = data.profiles.find((p) => p.id === i.to)!;
              return (
                <Card key={i.id}>
                  <Row between wrap>
                    <Column style={{ flex: 1, minWidth: 0 }} gap={4}>
                      <Button
                        kind="quiet"
                        label={`View invited profile ${p.username ?? p.id}`}
                        onPress={() => app.navigate(`person/${p.id}`)}
                      >
                        {p.name}
                      </Button>
                      <Copy muted size={12}>
                        @{p.username ?? p.id} · Awaiting acceptance
                      </Copy>
                    </Column>
                    <Button
                      kind="secondary"
                      label={`Uninvite ${p.username ?? p.id}`}
                      disabled={app.busy}
                      onPress={() =>
                        void app.act({ type: 'invitationCancel', id: i.id }, 'Invitation cancelled')
                      }
                    >
                      Uninvite
                    </Button>
                  </Row>
                </Card>
              );
            })}
          {!pendingInvites.length && <Copy muted>No pending invites.</Copy>}
        </Column>
      ) : (
        v.members
          .filter((id) => {
            const p = data.profiles.find((p) => p.id === id);
            return p && matchesTeam(p, query, kind);
          })
          .map((id) => {
            const p = data.profiles.find((p) => p.id === id)!;
            const metrics = commitmentMetrics(
              data.commitments.filter((c) => c.ventureId === v.id),
              id,
            );
            return (
              <Card key={id}>
                <Row>
                  <Avatar person={p} />
                  <Column gap={4} style={{ flex: 1 }}>
                    <Heading size={20}>{p.name}</Heading>
                    <Copy muted size={13}>
                      {p.hours} hrs / week · {p.style}
                    </Copy>
                  </Column>
                </Row>
                <Row wrap>
                  {p.skills.map((s) => (
                    <Tag key={s}>{s}</Tag>
                  ))}
                </Row>
                <Copy muted size={13}>
                  {metrics.completed} of {metrics.total} commitments completed in this venture.{' '}
                  {metrics.onTime} completed on time.
                </Copy>
                <Row>
                  <Button kind="quiet" onPress={() => app.navigate(`person/${id}`)}>
                    View profile
                  </Button>
                  {id !== CURRENT_USER && (
                    <Button kind="secondary" onPress={() => setEndorse(id)}>
                      Record endorsement
                    </Button>
                  )}
                </Row>
              </Card>
            );
          })
      )}
      <Copy muted size={12}>
        Work history provides context for a conversation. It is not a public ranking or an external
        verification.
      </Copy>
      {invite && <InviteSearch venture={v} onClose={() => setInvite(false)} />}
      {endorse && (
        <FormSheet
          title="Recognise a teammate"
          fields={[
            {
              key: 'skill',
              label: 'A skill they demonstrated',
              type: 'select',
              options: data.profiles.find((p) => p.id === endorse)!.skills,
              required: true,
            },
            { key: 'text', label: 'What did you appreciate?', type: 'long', required: true },
          ]}
          initial={{ skill: '', text: '' }}
          onClose={() => setEndorse(null)}
          submit="Record endorsement"
          onSave={(values) =>
            app.act(
              {
                type: 'endorse',
                endorsement: {
                  id: newId('endorsement'),
                  from: CURRENT_USER,
                  to: endorse,
                  ventureId: v.id,
                  skill: String(values.skill),
                  text: String(values.text),
                },
              },
              'Endorsement recorded',
            )
          }
        />
      )}
      {owner && (
        <Button kind="secondary" onPress={onReview}>
          Pending requests (
          {
            data.invitations.filter(
              (i) => i.ventureId === v.id && i.kind === 'request' && i.status === 'pending',
            ).length
          }
          )
        </Button>
      )}
    </Column>
  );
}
export function VentureScreen({ ventureId }: { ventureId: string }) {
  const data = useData();
  const app = useApp();
  const me = useMe();
  const tab = app.ventureTabs[ventureId] ?? 'VentureBot';
  const setTab = (value: string) => app.setVentureTab(ventureId, value);
  const [request, setRequest] = useState(false);
  const v = data.ventures.find((v) => v.id === ventureId);
  if (!v)
    return (
      <Page title="Venture not found" back="ventures">
        <Empty
          title="This room isn’t available"
          detail="Find a venture from Discover or start one of your own."
          action={<Button onPress={() => app.navigate('ventures')}>Your ventures</Button>}
        />
      </Page>
    );
  const member = v.members.includes(CURRENT_USER);
  const requested = data.invitations.some(
    (i) => i.from === CURRENT_USER && i.ventureId === v.id && i.status === 'pending',
  );
  if (tab === 'Pending requests' && member)
    return <PendingRequests venture={v} onBack={() => setTab('Team')} />;
  return (
    <Page title={v.name} back={member ? 'ventures' : 'discover'}>
      {!!v.logo && (
        <Image
          source={{ uri: v.logo }}
          accessibilityLabel={`${v.name} logo`}
          style={{ width: 80, height: 80, borderRadius: 18 }}
          resizeMode="contain"
        />
      )}
      {request && (
        <FormSheet
          title="Request to collaborate"
          intro="Would you like to attach a collaboration note? It is optional."
          fields={[{ key: 'note', label: 'Collaboration note (optional)', type: 'long' }]}
          initial={{ note: '' }}
          submit="Send request"
          onClose={() => setRequest(false)}
          onSave={(values) =>
            app.act({ type: 'join', ventureId: v.id, note: String(values.note) }, 'Request sent')
          }
        />
      )}
      <Row wrap>
        <Tag accent>{v.stage}</Tag>
        <Tag>{v.industry}</Tag>
        <Tag>{v.preference}</Tag>
        <Tag>{v.hours} hrs / week</Tag>
      </Row>
      {member ? (
        (() => {
          const isFounder = me.role === 'Business Owner' || me.role === 'Both';
          const hasStats =
            (isFounder && ['pro', 'enterprise', 'ultimate', 'elite', 'agency'].includes(me.subscriptionTier ?? '')) ||
            v.upgrades.includes('website_stats');
          const tabsList = ['VentureBot', 'Meeting room', 'Overview'];
          tabsList.push('Decisions', 'Tasks', 'Calendar', 'Contributions', 'Team');
          if (hasStats) tabsList.push('Website Stats');
          if (v.members[0] === CURRENT_USER) tabsList.push('Upgrades', 'Settings');

          return (
            <>
              <Segments items={tabsList} value={tab} onChange={setTab} />
              {tab === 'Settings' && v.members[0] === CURRENT_USER ? (
                <VentureSettings venture={v} onCancel={() => setTab('Overview')} />
              ) : tab === 'Overview' ? (
                <Overview venture={v} />
              ) : tab === 'Decisions' ? (
                <DecisionPanel venture={v} />
              ) : tab === 'Tasks' ? (
                <CommitmentPanel venture={v} />
              ) : tab === 'Calendar' ? (
                <CalendarPanel venture={v} />
              ) : tab === 'Contributions' ? (
                <ContributionPanel venture={v} />
              ) : tab === 'Meeting room' ? (
                <ChatPane>
                  <Conversation channel={v.id} />
                </ChatPane>
              ) : tab === 'VentureBot' ? (
                <ChatPane>
                  <Conversation channel={`venturebot-${v.id}`} />
                </ChatPane>
              ) : tab === 'Website Stats' ? (
                <WebsiteStatsPanel venture={v} />
              ) : tab === 'Upgrades' && v.members[0] === CURRENT_USER ? (
                <UpgradesPanel venture={v} />
              ) : (
                <Team venture={v} onReview={() => setTab('Pending requests')} />
              )}
            </>
          );
        })()
      ) : (
        <>
          <Card>
            <Heading size={23}>The idea</Heading>
            <Copy>{v.description}</Copy>
            <Copy muted>{v.problem}</Copy>
            <Copy>{v.solution}</Copy>
            <Heading size={20}>The next milestone</Heading>
            <Copy>{v.milestone}</Copy>
          </Card>
          <Card>
            <Heading size={22}>A space for your strengths</Heading>
            <Row wrap>
              {v.seeking.map((s) => (
                <Tag key={s} accent>
                  {s}
                </Tag>
              ))}
            </Row>
            <Copy muted>
              {v.location} · {v.cadence}
            </Copy>
            <Row>
              {v.members.map((id) => (
                <Avatar
                  key={id}
                  person={
                    data.profiles.find((p) => p.id === id) ??
                    v.memberSummaries?.find((p) => p.id === id) ?? {
                      name: 'Collaborator',
                      avatar: 'sage',
                    }
                  }
                />
              ))}
            </Row>
            <Copy muted size={13}>
              Skills on the team:{' '}
              {[
                ...new Set(
                  v.members.flatMap(
                    (id) =>
                      (
                        data.profiles.find((p) => p.id === id) ??
                        v.memberSummaries?.find((p) => p.id === id)
                      )?.skills ?? [],
                  ),
                ),
              ].join(', ')}
            </Copy>
            <Button disabled={v.stage === 'Closed' || requested} onPress={() => setRequest(true)}>
              {requested
                ? 'Request pending'
                : v.stage === 'Closed'
                  ? 'This venture has closed'
                  : 'Request to collaborate'}
            </Button>
            <Copy muted size={12}>
              The team’s conversations and work records are visible after you join.
            </Copy>
          </Card>
        </>
      )}
    </Page>
  );
}

function ventureDraft(v: Venture): FormValues {
  return Object.fromEntries(
    ventureDetailFields.map((field) => {
      const value = v[field.key as keyof Venture];
      return [
        field.key,
        field.type === 'multi'
          ? Array.isArray(value)
            ? value
            : []
          : field.type === 'number'
            ? Number(value ?? 0)
            : String(value ?? ''),
      ];
    }),
  ) as FormValues;
}
function VentureSettings({ venture: v, onCancel }: { venture: Venture; onCancel: () => void }) {
  const app = useApp(),
    t = useTheme();
  const [draft, setDraft] = useState<FormValues>(() => ventureDraft(v));
  const [validation, setValidation] = useState('');
  async function save() {
    const missing = ventureDetailFields.find(
      (f) => f.required && !String(draft[f.key] ?? '').trim(),
    );
    if (missing) {
      setValidation(`Please complete ${missing.label.toLowerCase()}.`);
      return;
    }
    setValidation('');
    await app.act(
      {
        type: 'ventureUpdate',
        ventureId: v.id,
        changes: { ...draft, hours: Number(draft.hours || 0) } as Extract<
          Command,
          { type: 'ventureUpdate' }
        >['changes'],
      },
      'Venture settings saved',
    );
  }
  return (
    <Column gap={24}>
      <Heading size={22}>Venture settings</Heading>
      <FormFields
        fields={ventureDetailFields}
        values={draft}
        onChange={(key, value) => {
          setDraft((old) => ({ ...old, [key]: value }));
          setValidation('');
        }}
      />
      <PremiumWork venture={v} />
      <Row wrap>
        <Button disabled={app.busy} onPress={() => void save()}>
          Save changes
        </Button>
        <Button
          disabled={app.busy}
          kind="secondary"
          onPress={() => {
            setDraft(ventureDraft(v));
            setValidation('');
            onCancel();
          }}
        >
          Cancel
        </Button>
      </Row>
      {!!validation && (
        <View
          accessibilityRole="alert"
          style={{ padding: 8, borderWidth: 1, borderRadius: 8, borderColor: t.danger }}
        >
          <Copy color={t.danger} size={12}>
            {validation}
          </Copy>
        </View>
      )}
    </Column>
  );
}

function PremiumWork({ venture: v }: { venture: Venture }) {
  const app = useApp();
  const [quote, setQuote] = useState<{
    available: boolean;
    amount?: number;
    currency?: string;
    interval?: string;
  }>();
  const workPrice = app.workPrice;
  useEffect(() => {
    let active = true;
    void workPrice()
      .then((value) => {
        if (active) setQuote(value);
      })
      .catch(() => {
        if (active) setQuote({ available: false });
      });
    return () => {
      active = false;
    };
  }, [workPrice]);
  const currency = new Intl.NumberFormat('en', {
    style: 'currency',
    currency: quote?.currency ?? 'GBP',
  });
  if (v.premiumWork)
    return (
      <Copy muted size={12}>
        Premium AI work and file transfer active
      </Copy>
    );
  if (v.members[0] !== CURRENT_USER) return null;
  return (
    <Column gap={6}>
      <Button
        kind="secondary"
        disabled={!quote?.available}
        onPress={() => void app.checkoutWork(v.id)}
      >
        {quote?.available
          ? `Enable Premium AI work · ${currency.format((quote.amount ?? 0) / 10 ** (currency.resolvedOptions().maximumFractionDigits ?? 2))} / ${quote.interval}`
          : 'Premium AI work and file transfer · coming soon'}
      </Button>
      <Copy muted size={12}>
        Work starts after payment is verified. A daily work allowance applies; completed files
        appear in the venture chat.
      </Copy>
    </Column>
  );
}

function WebsiteStatsPanel({ venture: v }: { venture: Venture }) {
  const me = useMe();
  const app = useApp();

  const hasAccess =
    v.upgrades.includes('website_stats') ||
    ['pro', 'enterprise', 'ultimate', 'elite', 'agency'].includes(me.subscriptionTier ?? '');

  if (!hasAccess) {
    return (
      <Card>
        <Column gap={12} style={{ alignItems: 'center', padding: 24 }}>
          <Heading size={24}>Website Stats</Heading>
          <Copy size={16} muted>
            The Pro plan includes tracking for your live store’s traffic, sales, and checkout
            behavior directly inside your venture.
          </Copy>
          <Button kind="primary" onPress={() => app.navigate('settings')}>
            Upgrade in Settings
          </Button>
        </Column>
      </Card>
    );
  }

  const snippet = `<script src="${Platform.OS === 'web' ? window.location.origin : 'https://ventureroom.procuraq.com'}/tracker.js?id=${encodeURIComponent(v.id)}"></script>`;

  // Calculate totals for today
  const date = new Date().toISOString().slice(0, 10);
  const todayStats = v.dailyStats?.[date] || {
    views: 0,
    visitors: 0,
    checkouts: 0,
    sales: 0,
    revenue: 0,
  };

  return (
    <Column gap={16}>
      <Row between>
        <Heading size={22}>Live Website Stats</Heading>
      </Row>

      <Card>
        <Column gap={8}>
          <Copy bold>Installation Snippet</Copy>
          <Copy muted size={13}>
            Paste this script into the &lt;head&gt; of your website to start tracking traffic
            universally.
          </Copy>
          {Platform.OS === 'web' ? (
            <textarea
              readOnly
              value={snippet}
              style={{
                padding: 12,
                borderRadius: 6,
                border: '1px solid #ccc',
                backgroundColor: '#f1f5f9',
                fontFamily: 'monospace',
                width: '100%',
                resize: 'none',
              }}
              rows={2}
            />
          ) : (
            <Copy size={12}>{snippet}</Copy>
          )}
        </Column>
      </Card>

      <Row wrap>
        <Card style={{ flex: 1, minWidth: 200, backgroundColor: '#0f172a' }}>
          <Copy bold style={{ color: '#94a3b8' }}>
            Total Views (Today)
          </Copy>
          <Copy size={28} bold>
            {todayStats.views}
          </Copy>
          <Copy size={13} style={{ color: '#cbd5e1' }}>
            Pages loaded today
          </Copy>
        </Card>
        <Card style={{ flex: 1, minWidth: 200 }}>
          <Copy bold muted>
            Unique Visitors (Today)
          </Copy>
          <Copy size={28} bold>
            {todayStats.visitors}
          </Copy>
          <Copy size={13} muted>
            Distinct users tracked today
          </Copy>
        </Card>
      </Row>

      <Row wrap>
        <Card style={{ flex: 1, minWidth: 200 }}>
          <Copy bold muted>
            Sales Revenue (Today)
          </Copy>
          <Copy size={28} bold>
            ${todayStats.revenue?.toFixed(2) || '0.00'}
          </Copy>
          <Copy size={13} style={{ color: '#10b981' }}>
            {todayStats.sales || 0} sales today
          </Copy>
        </Card>
        <Card style={{ flex: 1, minWidth: 200 }}>
          <Copy bold muted>
            Checkout Funnel (Today)
          </Copy>
          <Copy size={28} bold>
            {todayStats.checkouts || 0}
          </Copy>
          <Copy size={13} muted>
            Users started checkout today
          </Copy>
        </Card>
      </Row>

      <Card>
        <Column gap={4}>
          <Copy bold>How to track sales and checkouts:</Copy>
          <Copy size={13} muted>
            The script above automatically tracks pageviews. To track deeper funnel events, call
            these functions from your website code (e.g. when a user clicks 'Buy' or completes
            payment):
          </Copy>
          <Copy
            size={13}
            style={{
              fontFamily: 'monospace',
              padding: 8,
              backgroundColor: '#f1f5f9',
              borderRadius: 4,
              marginTop: 8,
            }}
          >
            window.vrTrack('checkout');
          </Copy>
          <Copy
            size={13}
            style={{
              fontFamily: 'monospace',
              padding: 8,
              backgroundColor: '#f1f5f9',
              borderRadius: 4,
            }}
          >
            window.vrTrack('sale', 49.99);
          </Copy>
        </Column>
      </Card>

      <Copy muted size={12}>
        Browser-reported counters are unverified telemetry. They do not verify payments or
        independently establish reputation.
      </Copy>
    </Column>
  );
}

export function VenturePreviewScreen({ ventureId }: { ventureId: string }) {
  const data = useData();
  const app = useApp();
  const v = data.profiles.flatMap((p) => p.ventureSummaries ?? []).find((x) => x.id === ventureId);
  const member = data.ventures.some((x) => x.id === ventureId && x.members.includes(CURRENT_USER));
  return (
    <Page title={v?.name ?? 'Venture unavailable'} back="discover">
      {v ? (
        <Column gap={20}>
          <Copy size={18}>{v.pitch}</Copy>
          <Row>
            <Tag>{v.industry}</Tag>
            <Tag>{v.stage}</Tag>
            <Tag>{v.memberCount} members</Tag>
          </Row>
          <Heading size={20}>Mission</Heading>
          <Copy>{v.mission}</Copy>
          <Heading size={20}>About this venture</Heading>
          <Copy>{v.description}</Copy>
          <VentureWebsite url={v.websiteUrl} />
          {member ? (
            <Button onPress={() => app.navigate(`venture/${v.id}`)}>Open venture workspace</Button>
          ) : (
            <Copy muted size={12}>
              Public overview. Project conversations and records are private to its members.
            </Copy>
          )}
        </Column>
      ) : (
        <Empty
          title="This public overview is unavailable"
          detail="Return to the profile to see their current ventures."
        />
      )}
    </Page>
  );
}


function UpgradesPanel({ venture: v }: { venture: Venture }) {
  const app = useApp();
  const [amount, React_useState_amount] = React.useState('10');
  const [isShared, React_useState_isShared] = React.useState(true);
  
  const val = parseFloat(amount) || 0;
  const platformFee = val * 0.2;
  const aiAllocation = val * 0.8;
  const estimatedWords = Math.floor(aiAllocation * 31250);
  const awaitingRealBilling = app.authenticated;

  const hasWebStats = v.upgrades?.includes('website_stats');
  const hasFiles = v.upgrades?.includes('file_uploads');

  return (
    <Column gap={16}>
      <Card>
        <Heading size={22}>Top Up Venture Credits</Heading>
        <Copy muted size={13}>
          Each venture starts with 10,000 free AI words. Top up directly for this venture below.
          VentureRoom charges a 20% platform fee. The remaining 80% is converted into AI generation words.
        </Copy>
        <Column gap={12} style={{ marginTop: 12 }}>
          <Row wrap style={{ alignItems: 'center', gap: 12 }}>
            <Copy>£</Copy>
            <input
              type="number"
              value={amount}
              onChange={(e) => React_useState_amount(e.target.value)}
              min={5}
              style={{
                width: 100,
                padding: '8px 12px',
                borderRadius: 6,
                border: '1px solid #ccc',
              }}
            />
          </Row>

          <div style={{ backgroundColor: '#f8fafc', padding: 12, borderRadius: 6, border: '1px solid #e2e8f0' }}>
            <Copy size={13} style={{ marginBottom: 4 }}>
              <strong>Platform fee:</strong> £{platformFee.toFixed(2)}
            </Copy>
            <Copy size={13} style={{ marginBottom: 4 }}>
              <strong>For AI Compute:</strong> £{aiAllocation.toFixed(2)}
            </Copy>
            <Copy size={13} style={{ color: '#059669' }}>
              <strong>Estimated Words:</strong> ~{estimatedWords.toLocaleString()}
            </Copy>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={isShared}
              onChange={(e) => React_useState_isShared(e.target.checked)}
            />
            <Copy size={13}>Allow other people on the team to use these credits</Copy>
          </label>

          <Button
            disabled={val < 5 || awaitingRealBilling}
            onPress={async () => {
              app.act(
                { type: 'topUpCredits', ventureId: v.id, amount: val, isShared },
                'Credits successfully topped up!'
              );
              React_useState_amount('10');
            }}
          >
            {awaitingRealBilling ? 'Coming soon' : 'Try in demo'}
          </Button>
        </Column>
      </Card>

      <Heading size={20}>Specific Venture Upgrades</Heading>
      <Copy muted>Unlock specific features for this venture only (£20/mo each) without upgrading your global subscription.</Copy>

      <Row wrap>
        <Card style={{ flex: 1, minWidth: 280 }}>
          <Column gap={12}>
            <Heading size={18}>Website Stats</Heading>
            <Copy size={14} muted>Track live pageviews, checkout funnels, and sales revenue for this venture directly in VentureRoom.</Copy>
            <Heading size={16}>£20 / mo</Heading>
            {hasWebStats ? (
              <Button disabled onPress={() => {}}>Active</Button>
            ) : (
              <Button disabled={awaitingRealBilling} onPress={() => {
                app.act({ type: 'buyVentureUpgrade', ventureId: v.id, upgrade: 'website_stats' }, 'Website Stats upgrade activated!');
              }}>{awaitingRealBilling ? 'Coming soon' : 'Try in demo'}</Button>
            )}
          </Column>
        </Card>

        <Card style={{ flex: 1, minWidth: 280 }}>
          <Column gap={12}>
            <Heading size={18}>Document Uploads</Heading>
            <Copy size={14} muted>Upload files, emails, and reference documents securely for your AI VentureBot and team members.</Copy>
            <Heading size={16}>£20 / mo</Heading>
            {hasFiles ? (
              <Button disabled onPress={() => {}}>Active</Button>
            ) : (
              <Button disabled={awaitingRealBilling} onPress={() => {
                app.act({ type: 'buyVentureUpgrade', ventureId: v.id, upgrade: 'file_uploads' }, 'Document Uploads upgrade activated!');
              }}>{awaitingRealBilling ? 'Coming soon' : 'Try in demo'}</Button>
            )}
          </Column>
        </Card>
      </Row>
    </Column>
  );
}
